import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  ExecutionsHistoryErrorCode,
  ReportEventDrilldownData,
  ReportEventDrilldownRequest,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ExecutionsHistoryDashboardService from '@src/services/ExecutionsHistoryDashboardService';

const PAGE_SIZE = 25;

interface State {
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
  errorCode?: ExecutionsHistoryErrorCode;
  data?: ReportEventDrilldownData;
}

export const useReportEventDrilldown = (
  filters: Omit<ReportEventDrilldownRequest, 'section' | 'cursor' | 'pageSize'> | null,
) => {
  const serviceRef = useRef<ExecutionsHistoryDashboardService | null>(null);
  if (!serviceRef.current) serviceRef.current = new ExecutionsHistoryDashboardService();
  const generationRef = useRef(0);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<State>({ loading: false, loadingMore: false, error: false });
  const key = filters ? JSON.stringify({ filters, retry }) : null;
  // Parents may recreate filter arrays on every render; only a changed key should reload.
  const stableRequestRef = useRef<{ key: string | null; request: typeof filters }>({
    key: null,
    request: null,
  });
  if (stableRequestRef.current.key !== key) {
    stableRequestRef.current = { key, request: filters ? { ...filters } : null };
  }
  const request = stableRequestRef.current.request;

  useEffect(() => {
    const generation = ++generationRef.current;
    if (!request) {
      setState({ loading: false, loadingMore: false, error: false });
      return;
    }
    setState({ loading: true, loadingMore: false, error: false });
    void serviceRef
      .current!.getReportEventDrilldown({
        ...request,
        section: 'report-event-drilldown',
        pageSize: PAGE_SIZE,
      })
      .then(result => {
        if (generation !== generationRef.current) return;
        setState(
          result.status === 'ok'
            ? { loading: false, loadingMore: false, error: false, data: result.response.data }
            : { loading: false, loadingMore: false, error: true, errorCode: result.errorCode },
        );
      });
    return () => {
      generationRef.current += 1;
    };
  }, [request]);

  const loadMore = useCallback(() => {
    if (!request || !state.data?.nextCursor || state.loading || state.loadingMore) return;
    const generation = generationRef.current;
    const cursor = state.data.nextCursor;
    setState(previous => ({ ...previous, loadingMore: true }));
    void serviceRef
      .current!.getReportEventDrilldown({
        ...request,
        section: 'report-event-drilldown',
        pageSize: PAGE_SIZE,
        cursor,
      })
      .then(result => {
        if (generation !== generationRef.current) return;
        setState(previous => {
          if (result.status !== 'ok' || !previous.data) {
            if (previous.data) return { ...previous, loadingMore: false };
            return {
              ...previous,
              loadingMore: false,
              error: true,
              ...(result.status === 'error' ? { errorCode: result.errorCode } : {}),
            };
          }
          const incoming = result.response.data;
          const ids = new Set(previous.data.rows.map(row => row.id));
          return {
            loading: false,
            loadingMore: false,
            error: false,
            data: {
              ...incoming,
              rows: [...previous.data.rows, ...incoming.rows.filter(row => !ids.has(row.id))],
            },
          };
        });
      });
  }, [request, state.data?.nextCursor, state.loading, state.loadingMore]);

  const refresh = useCallback(() => setRetry(value => value + 1), []);
  return { ...state, loadMore, refresh };
};
